//@author: devin

import type { MultiselectModelI } from "./s25.multiselect.component";
import { inject, Injectable } from "@angular/core";
import { ItemService } from "../../util/items/item.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { jSith } from "../../util/jquery-replacement";
import { S25Util } from "../../util/s25-util";
import { MultiselectApi, MultiselectResultsApi } from "./s25.multiselect.api";
import { SearchCriteriaContext } from "../../services/search/search-criteria/search.criteria.context";
import type { SearchCriteriaType } from "../../pojo/SearchCriteriaI";

@Injectable({ providedIn: "root" })
export class SearchCriteriaDataUtil {
    private searchCriteriaContext = inject(SearchCriteriaContext);
    private readonly itemService = inject(ItemService);

    /** Restrict items to allowed IDs, replace origItems with a clone of that list, trim selections not in the set. */
    public applyAllowedItemIdsFilter(multiSelectBean: MultiselectModelI): void {
        const raw = multiSelectBean.allowedItemIds;
        if (!raw?.length) {
            return;
        }
        const allowed = new Set(raw.map((id) => Number(id)));
        const keep = (item: S25ItemI) => allowed.has(Number(item.itemId));

        multiSelectBean.items = (multiSelectBean.items || []).filter(keep);
        multiSelectBean.origItems = S25Util.array.clone(multiSelectBean.items);

        if (multiSelectBean.selectedItems?.length) {
            multiSelectBean.selectedItems = multiSelectBean.selectedItems.filter(keep);
        }
        if (multiSelectBean.uncommittedSelectedItems?.length) {
            multiSelectBean.uncommittedSelectedItems = multiSelectBean.uncommittedSelectedItems.filter(keep);
        }
    }

    public mergeItemsInfo = (sources: S25ItemI[], targets: S25ItemI[]) => {
        if (!sources?.length || !targets?.length) return;
        // Create a dictionary from sources for efficient lookup
        const sourceDict = S25Util.fromEntries(sources.map((source) => [source.itemId, source]));
        // Iterate through all targets (including duplicates) to merge info from sources
        targets.forEach((target) => {
            const source = sourceDict[target.itemId];
            if (source) {
                target.favorite = source.favorite;
                target.itemDesc = source.itemDesc;
                target.itemName = target.itemName || source.itemName;
                const intermediate = Object.assign({}, source, target);
                Object.assign(target, intermediate);
            }
        });
    };

    /**
     * Enriches items with missing itemName values by fetching from searchCriteria service methods.
     * Only fetches data if there are items missing itemName.
     *
     * @param type - The searchCriteria type (e.g., "organizationRoles")
     * @param itemsToEnrich - Array of items that may be missing itemName
     * @param options - Optional parameters for the service method (useSecurity defaults to true)
     * @returns Promise that resolves with the enriched items array
     */
    public async enrichItemsWithItemName(
        type: SearchCriteriaType["type"],
        itemsToEnrich: S25ItemI.NumId[],
        options?: { useSecurity?: boolean; activeOnly?: boolean; customFilterValue?: string },
    ): Promise<S25ItemI[]> {
        // Filter to only items missing itemName - early return if none missing
        const itemsMissingName = itemsToEnrich.filter((item) => !item.itemName);
        if (itemsMissingName.length === 0) {
            return jSith.when(itemsToEnrich);
        }

        const context = this.searchCriteriaContext.Context[type];
        if (!context || !context.serviceMethod) {
            return jSith.when(itemsToEnrich);
        }

        // Don't handle expensiveSearch items yet - log error and return original array
        if (context.expensiveSearch) {
            console.error(
                `enrichItemsWithItemName: expensiveSearch not yet supported for type "${type}". Returning items unchanged.`,
            );
            return jSith.when(itemsToEnrich);
        }

        return this.itemService.criteria(type).fetchAll(itemsToEnrich, { hydrate: true });
    }

    public searchCriteriaSetup(
        multiSelectBean?: MultiselectModelI,
        searchContext?: any, // SearchCriteriaType["type"],
        selectedItems?: S25ItemI[],
        elem?: any,
        honorMatching?: boolean,
        permanentCustomFilterValue?: string,
        useSecurity?: boolean,
        eventTypeId?: number,
    ) {
        let itemsMerged = false;
        multiSelectBean = multiSelectBean || {};
        const origOnInit =
            multiSelectBean.onInit ||
            function () {
                return jSith.when();
            };
        selectedItems = selectedItems || [];
        honorMatching = honorMatching || false; //default to false; unless true, matching bean and show matching are NOT included in the multiselect model; if true and the criteria bean has it configured, then it will show
        const ret = jSith.defer();
        S25Util.coalesceDeep(multiSelectBean, searchContext); //inherit searchContext settings but prefer initial modelBean properties if present
        if (!honorMatching) {
            multiSelectBean.showMatching = false;
        }
        multiSelectBean.dataDefer = jSith.defer();
        multiSelectBean.dataPromise = multiSelectBean.dataDefer.promise; //data promise so that infinite scroll can wait until data is loaded

        if (multiSelectBean.domainFilter) {
            //get options to choose on multiselect, eg for events, a list of searches
            multiSelectBean.domainFilter.promise = multiSelectBean.domainFilter.filterMethod().then((data: any) => {
                data = (S25Util.array.isArray(data) && data) || multiSelectBean.extractItems(data);
                multiSelectBean.domainFilter.options = [{ itemName: "All filters", itemId: -999 }].concat(data);
            });
        }

        const resetPagination = (data: any) => {
            //function to reset pagination when user makes a new type of filter
            multiSelectBean.pageCount = data.searchCriteria.page_count;
            multiSelectBean.cacheId = data.searchCriteria.cache_id;
            multiSelectBean.page = 1; //page was 0, now we got the first page, so we increment to 1
        };

        const refresh = (doPageReset: boolean, setItems: boolean, isMultiselectRefresh: boolean, data: any) => {
            doPageReset && resetPagination(data); //each filter data call resets the pages
            if (setItems) {
                const extractedMaybe =
                    (multiSelectBean.extractItems && multiSelectBean.extractItems(data)) ||
                    (S25Util.array.isArray(data) ? data : []);
                const priorItems = multiSelectBean.items;
                const priorIsArray = Array.isArray(priorItems);
                const priorLength = priorIsArray ? priorItems.length : 0;
                const useExtractedRows = !priorIsArray || extractedMaybe.length > priorLength;
                multiSelectBean.items = useExtractedRows ? extractedMaybe : priorItems;
                if (multiSelectBean.allowedItemIds?.length) {
                    this.applyAllowedItemIdsFilter(multiSelectBean);
                } else if (!multiSelectBean.origItems) {
                    multiSelectBean.origItems = multiSelectBean.items;
                }
                if (!itemsMerged) {
                    itemsMerged = true;
                    this.mergeItemsInfo(multiSelectBean.items, multiSelectBean.selectedItems);
                    multiSelectBean.showResult && MultiselectResultsApi.refresh(document.body, multiSelectBean.uuid);
                }
            }
            let resp = isMultiselectRefresh && MultiselectApi.refresh(document.body, multiSelectBean.uuid);
            resp = (resp && resp.then && resp) || jSith.when();
            resp.then(() => {
                MultiselectApi.forceScrollAction(document.body, multiSelectBean.uuid);
            });
            multiSelectBean.dataDefer.resolve(); //multiselect done method will reset this
            return (multiSelectBean.extractItems && multiSelectBean.extractItems(data)) || data;
        };

        if (multiSelectBean.hasScroll) {
            //if infinite scroll
            let skipFirstShow = false; //we may want to prefetch items before the user clicks to show the multiselect, that is what this is used for
            //multiSelectBean.columns = 4; //set multiselect to have 4 cols by default (all of these selections have many elements)
            multiSelectBean.page = 0; //set initial page info
            multiSelectBean.pageCount = 1;
            multiSelectBean.pageSize = 100;
            multiSelectBean.cacheId = 0;
            multiSelectBean.filter = undefined;

            const hasMoreF = () => {
                //infinite scroll uses this to determine if more pages exist
                return multiSelectBean.page < multiSelectBean.pageCount;
            };

            multiSelectBean.hasMoreF = hasMoreF; //set this bc multiselect uses it via its modelBean

            multiSelectBean.scrollF = (callback?: Function) => {
                //action on scroll
                callback = callback || function () {};
                const ret = {
                    pageCount: multiSelectBean.pageCount,
                    page: multiSelectBean.page,
                    items: <S25ItemI[]>[],
                };
                const callbackF = function () {
                    //callback function to refresh multiselect
                    callback();
                    setTimeout(() => MultiselectApi.refresh(document.body, multiSelectBean.uuid));
                    return ret;
                };
                if (hasMoreF()) {
                    //if more pages
                    return multiSelectBean
                        .serviceMethod(multiSelectBean.cacheId, multiSelectBean.page, multiSelectBean.pageSize)
                        .then((data: any) => {
                            //get paginated data
                            const items = multiSelectBean.extractItems(data); //extract items from data
                            if (items && items.length > 0) {
                                ret.page++; //increment page number
                                ret.items = multiSelectBean.items.concat(items); //set items of this next page
                                return callbackF(); //run callback
                            } else {
                                ret.pageCount = 0; //no more pages
                                return callbackF(); //run callback
                            }
                        });
                } else {
                    //no more pages
                    return jSith.when(callbackF()); //still run callback
                }
            };

            multiSelectBean.serverSideFilter = (filter) => {
                //convenience function for multiselect to call when filtering based on a string (name)
                //save page/search request in a promise, falling back to [] if not exists
                //this serializes the async requests so we can keep pagination and results data straight
                multiSelectBean.currentPromises = multiSelectBean.currentPromises || [];

                //set new pagination meta data
                multiSelectBean.filter = filter;
                multiSelectBean.cacheId = 0; //run a new search
                multiSelectBean.page = 0; //run from page 0 since this is a new filter

                //make call immediately
                const promise = multiSelectBean.serviceMethod(
                    multiSelectBean.cacheId,
                    multiSelectBean.page,
                    multiSelectBean.pageSize,
                    multiSelectBean.filter,
                    multiSelectBean.filterFavorites,
                    multiSelectBean.domainFilter,
                    (permanentCustomFilterValue || "") + (multiSelectBean.customFilterValue || ""),
                );

                //note we must immediately push to the array so the next caller, if any, can check if it is the last request so far
                multiSelectBean.currentPromises.push(promise); //push to promise array

                return promise.then((data: any) => {
                    //when promise finishes
                    const len = multiSelectBean.currentPromises.length;
                    //check this promise was the last one, if so, return data, else do nothing
                    if (len > 0 && promise === multiSelectBean.currentPromises[len - 1]) {
                        return jSith.when(refresh(true, false, false, data)); //return items
                    } else {
                        return jSith.when();
                    }
                });
            };

            multiSelectBean.onInit = () => {
                //multiselect runs this onInit
                return origOnInit().then(() => {
                    if (multiSelectBean.expensiveSearch) {
                        //for expensive searches, each search starts with its selected items
                        multiSelectBean.items = multiSelectBean.selectedItems || [];
                        MultiselectApi.refresh(document.body, multiSelectBean.uuid);
                        return jSith.when();
                    } else {
                        if (!skipFirstShow) {
                            //if skipFirstShow true, then skip first first show
                            return multiSelectBean
                                .serviceMethod(
                                    multiSelectBean.cacheId,
                                    multiSelectBean.page,
                                    multiSelectBean.pageSize,
                                    multiSelectBean.filter,
                                    multiSelectBean.filterFavorites,
                                    multiSelectBean.domainFilter,
                                    multiSelectBean.omitReadOnly,
                                )
                                .then((data: any) => {
                                    //get any new data into modelBean
                                    return refresh(true, true, true, data);
                                });
                        } else {
                            //do not skip further shows
                            skipFirstShow = false;
                        }
                    }
                });
            };

            multiSelectBean.items = []; //need to set to empty array to set selectedItems before we run data ajax call
            multiSelectBean.selectedItems = selectedItems; //set selectedItems
            multiSelectBean.preFetchItems &&
                multiSelectBean.onInit().then(() => {
                    //if context has selectedItems, then run data before Edit click, and resolve promise
                    skipFirstShow = true;
                    ret.resolve(); //if pre fetching, wait to resolve return promise until prefetch done
                });
            if (!multiSelectBean.preFetchItems) {
                ret.resolve();
            } //flag init done in scope for convenience
        } else {
            //if here, then we do not have to mess with infinite scroll
            multiSelectBean.items = []; //need to set to empty array to set selectedItems before we run data ajax call
            multiSelectBean.isItemsInit = false; //flag the fact that the first set of items was empty (so make the ajax call onInit)
            multiSelectBean.selectedItems = selectedItems; //set selectedItems
            const initialSelectedItems = S25Util.deepCopy(multiSelectBean.selectedItems); //save initially selected items

            multiSelectBean.onInit = () => {
                return origOnInit().then(function () {
                    if (!multiSelectBean.isItemsInit) {
                        multiSelectBean.isItemsInit = true;
                        multiSelectBean.items = undefined;
                    }
                    const promise = multiSelectBean.items
                        ? jSith.when(multiSelectBean.items)
                        : multiSelectBean.serviceMethod(
                              !searchContext.expensiveSearch && useSecurity,
                              eventTypeId,
                              permanentCustomFilterValue,
                          ); //if modelItems exist, use them, else call service for them
                    return promise.then((data: any) => {
                        //get any new data into modelBean

                        //keep selectedItem properties added later on initial selected items (like isPermanent)
                        jSith.forEach(multiSelectBean.selectedItems, (_: any, selectedItem) => {
                            jSith.forEach(initialSelectedItems, (_: any, initialSelectedItem) => {
                                if (initialSelectedItem.itemId == selectedItem.itemId) {
                                    initialSelectedItem.isPermanent = selectedItem.isPermanent;
                                    initialSelectedItem.isSecret = selectedItem.isSecret;
                                }
                            });
                        });

                        //always include initial selected items in full items data in case they were excluded for some reason from the GET for all items
                        data = S25Util.array.uniqueByProp(data.concat(initialSelectedItems), "itemId");
                        return refresh(false, true, true, data);
                    });
                });
            };

            ret.resolve();
        }

        //return promise
        return S25Util.all([
            ret.promise,
            (multiSelectBean.domainFilter && multiSelectBean.domainFilter.promise) || jSith.when(),
        ]);
    }
}
